import { normalizeRadians } from '@star-snake/shared'

export type NetworkRecoveryMode = 'normal' | 'soft' | 'hard'

export interface NetworkRecoverySample {
  rttMs: number
  inputAckMs: number
  pendingInputAckCount: number
}

export interface NetworkRecoveryState {
  mode: NetworkRecoveryMode
  candidateMode: NetworkRecoveryMode | null
  candidateSinceMs: number | null
}

export interface SentInputFingerprint {
  hasAim: boolean
  desiredHeading: number
  boost: boolean
}

export interface ShouldSendNetworkInputArgs {
  nowMs: number
  lastSentAtMs: number
  mode: NetworkRecoveryMode
  nextInput: SentInputFingerprint
  lastSentInput: SentInputFingerprint | null
}

export interface ShouldRequestNetworkResyncArgs {
  nowMs: number
  mode: NetworkRecoveryMode
  latestServerTick: number
  lastAckProgressAtMs: number
  lastAutoResyncAtMs: number
  sample: NetworkRecoverySample
}

const SOFT_RTT_MS = 450
const SOFT_INPUT_ACK_MS = 300
const SOFT_PENDING_ACK_COUNT = 24
const HARD_RTT_MS = 1200
const HARD_INPUT_ACK_MS = 700
const HARD_PENDING_ACK_COUNT = 80

const NORMAL_TO_SOFT_HOLD_MS = 1200
const NORMAL_TO_HARD_HOLD_MS = 700
const SOFT_TO_HARD_HOLD_MS = 700
const SOFT_TO_NORMAL_HOLD_MS = 1800
const HARD_TO_SOFT_HOLD_MS = 1500
const HARD_TO_NORMAL_HOLD_MS = 2000

const HEADING_CHANGE_EPSILON = 0.03
const NORMAL_INPUT_SEND_INTERVAL_MS = 1000 / 60
const SOFT_INPUT_SEND_INTERVAL_MS = 1000 / 30
const HARD_INPUT_SEND_INTERVAL_MS = 1000 / 20
const SOFT_REDUNDANT_REFRESH_INTERVAL_MS = 180
const HARD_REDUNDANT_REFRESH_INTERVAL_MS = 300
const MIN_CHANGED_COMMAND_INTERVAL_MS = 12

const HARD_RESYNC_MIN_INTERVAL_MS = 2500
const ACK_PROGRESS_STALL_MS = 1500
const RESYNC_PENDING_ACK_COUNT = 96
const RESYNC_HIGH_RTT_MS = 2500
const RESYNC_HIGH_INPUT_ACK_MS = 1200

export function createInitialNetworkRecoveryState(): NetworkRecoveryState {
  return {
    mode: 'normal',
    candidateMode: null,
    candidateSinceMs: null,
  }
}

export function classifyNetworkRecoveryMode(sample: NetworkRecoverySample): NetworkRecoveryMode {
  if (
    sample.rttMs >= HARD_RTT_MS
    || sample.inputAckMs >= HARD_INPUT_ACK_MS
    || sample.pendingInputAckCount >= HARD_PENDING_ACK_COUNT
  ) {
    return 'hard'
  }

  if (
    sample.rttMs >= SOFT_RTT_MS
    || sample.inputAckMs >= SOFT_INPUT_ACK_MS
    || sample.pendingInputAckCount >= SOFT_PENDING_ACK_COUNT
  ) {
    return 'soft'
  }

  return 'normal'
}

export function updateNetworkRecoveryState(
  state: NetworkRecoveryState,
  sample: NetworkRecoverySample,
  nowMs: number,
): NetworkRecoveryState {
  const targetMode = classifyNetworkRecoveryMode(sample)
  if (targetMode === state.mode) {
    if (state.candidateMode === null && state.candidateSinceMs === null) return state
    return {
      mode: state.mode,
      candidateMode: null,
      candidateSinceMs: null,
    }
  }

  if (state.candidateMode !== targetMode || state.candidateSinceMs === null) {
    return {
      mode: state.mode,
      candidateMode: targetMode,
      candidateSinceMs: nowMs,
    }
  }

  const holdMs = getTransitionHoldMs(state.mode, targetMode)
  if (nowMs - state.candidateSinceMs < holdMs) return state

  return {
    mode: targetMode,
    candidateMode: null,
    candidateSinceMs: null,
  }
}

export function getAdaptiveInputSendIntervalMs(mode: NetworkRecoveryMode): number {
  switch (mode) {
    case 'soft':
      return SOFT_INPUT_SEND_INTERVAL_MS
    case 'hard':
      return HARD_INPUT_SEND_INTERVAL_MS
    case 'normal':
    default:
      return NORMAL_INPUT_SEND_INTERVAL_MS
  }
}

export function shouldSendNetworkInput(args: ShouldSendNetworkInputArgs): boolean {
  const {
    nowMs,
    lastSentAtMs,
    mode,
    nextInput,
    lastSentInput,
  } = args

  if (lastSentAtMs <= 0 || !lastSentInput) return true

  const elapsedMs = nowMs - lastSentAtMs
  if (mode === 'normal') {
    return elapsedMs >= NORMAL_INPUT_SEND_INTERVAL_MS
  }

  const changed = hasMeaningfulInputChange(lastSentInput, nextInput)
  if (changed) {
    // Degraded recovery suppresses redundant refreshes, not live steering.
    // Changed commands must keep normal cadence so the player does not lose control.
    return elapsedMs >= Math.max(MIN_CHANGED_COMMAND_INTERVAL_MS, NORMAL_INPUT_SEND_INTERVAL_MS)
  }

  return elapsedMs >= getRedundantRefreshIntervalMs(mode)
}

export function shouldRequestNetworkResync(args: ShouldRequestNetworkResyncArgs): boolean {
  const {
    nowMs,
    mode,
    latestServerTick,
    lastAckProgressAtMs,
    lastAutoResyncAtMs,
    sample,
  } = args

  if (mode !== 'hard' || latestServerTick <= 0) return false
  if (lastAutoResyncAtMs > 0 && nowMs - lastAutoResyncAtMs < HARD_RESYNC_MIN_INTERVAL_MS) return false

  if (sample.pendingInputAckCount >= RESYNC_PENDING_ACK_COUNT) return true

  if (
    sample.pendingInputAckCount >= 48
    && (sample.rttMs >= RESYNC_HIGH_RTT_MS || sample.inputAckMs >= RESYNC_HIGH_INPUT_ACK_MS)
  ) {
    return true
  }

  if (lastAckProgressAtMs <= 0) return false
  return sample.pendingInputAckCount >= 24 && nowMs - lastAckProgressAtMs >= ACK_PROGRESS_STALL_MS
}

function getTransitionHoldMs(from: NetworkRecoveryMode, to: NetworkRecoveryMode): number {
  if (from === 'normal' && to === 'soft') return NORMAL_TO_SOFT_HOLD_MS
  if (from === 'normal' && to === 'hard') return NORMAL_TO_HARD_HOLD_MS
  if (from === 'soft' && to === 'hard') return SOFT_TO_HARD_HOLD_MS
  if (from === 'soft' && to === 'normal') return SOFT_TO_NORMAL_HOLD_MS
  if (from === 'hard' && to === 'soft') return HARD_TO_SOFT_HOLD_MS
  if (from === 'hard' && to === 'normal') return HARD_TO_NORMAL_HOLD_MS
  return NORMAL_TO_SOFT_HOLD_MS
}

function getRedundantRefreshIntervalMs(mode: NetworkRecoveryMode): number {
  switch (mode) {
    case 'soft':
      return SOFT_REDUNDANT_REFRESH_INTERVAL_MS
    case 'hard':
      return HARD_REDUNDANT_REFRESH_INTERVAL_MS
    case 'normal':
    default:
      return NORMAL_INPUT_SEND_INTERVAL_MS
  }
}

function hasMeaningfulInputChange(previous: SentInputFingerprint, next: SentInputFingerprint): boolean {
  if (previous.boost !== next.boost) return true
  if (previous.hasAim !== next.hasAim) return true
  return Math.abs(normalizeRadians(next.desiredHeading - previous.desiredHeading)) >= HEADING_CHANGE_EPSILON
}
